/**
 * Finance home — the purchase ledger at a glance.
 *
 * The finance team's landing page, reached through the home-persona switch.
 * It answers the three questions they open the day with: what is waiting to
 * be confirmed, what falls due before it is released for payment, and which
 * suppliers owe us an invoice. Every figure links to the page where it is
 * worked.
 *
 * One composed read (`/views/finance-home`), built from the same bases as the
 * invoice queue and the open purchase orders, so each figure is the count of
 * the page it links to.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { accrualsErrorMessage } from "@shared-core/data/api/accruals-and-prepayments";
import type { FinanceHomeView } from "@shared-core/data/purchase-ledger-types";
import {
  monthJustEnded,
  monthLabel,
} from "@shared-core/domain/accruals-and-prepayments";
import { emptyListQuery } from "@shared-core/domain/object-ui/list-query";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { useLoad } from "@shared-core/domain/useLoad";
import {
  ageLabel,
  formatDate,
  formatMoney,
} from "@shared-core/domain/purchase-ledger";
import {
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import { paths } from "@shared-core/routing/routes";
import { HomePersonaToggle } from "../app/components/patterns/HomePersonaToggle";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Skeleton } from "../app/components/primitives/skeleton";
import { cn } from "../app/components/primitives/utils";
import { currentTime } from "@shared-core/domain/current-time";

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; home: FinanceHomeView };

/** The month end object (`finance_accruals_and_prepayments_module`): its list is the card's read. */
const MONTH_END_OBJECT = "finance_accruals_and_prepayments.month_end";

export function FinanceHome() {
  const navigate = useNavigate();
  // Role only supplies the default; the on-page toggle always sets one.
  const { setPersona } = useHomePersona({ role: undefined });
  const switchPersona = (next: HomePersona) => {
    setPersona(next);
    navigate(homePathFor(next));
  };

  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    adapter
      .getFinanceHome()
      .then((home) => {
        if (!cancelled) setState({ kind: "loaded", home });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "The purchase ledger did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl">Purchase ledger</h1>
          <HomePersonaToggle current="finance" onSelect={switchPersona} />
        </div>
        <p className="text-muted-foreground text-sm">
          Supplier invoices to confirm, purchase invoices falling due, and goods
          still waiting for an invoice.
        </p>
      </div>

      {state.kind === "loading" && (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          aria-label="Loading the purchase ledger"
        >
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}
      {state.kind === "failed" && (
        <Alert variant="destructive">
          <AlertDescription>
            Could not load the purchase ledger: {state.message}
          </AlertDescription>
        </Alert>
      )}
      {state.kind === "loaded" && <LedgerAtAGlance home={state.home} />}
    </div>
  );
}

function LedgerAtAGlance({ home }: { home: FinanceHomeView }) {
  const now = currentTime();
  const { summary, countsByTab: counts, dueSoon, missing } = home;
  const hasMonthEnd = isFeatureEnabled("accruals_and_prepayments");
  return (
    <>
      <div
        className={cn(
          "grid grid-cols-1 gap-4 sm:grid-cols-2",
          hasMonthEnd ? "xl:grid-cols-5" : "xl:grid-cols-4",
        )}
      >
        <StatCard
          title="Invoices to confirm"
          value={String(summary.count)}
          detail={
            summary.oldestReceivedAt
              ? `${formatMoney(summary.net)} net · oldest arrived ${ageLabel(summary.oldestReceivedAt, now)} ago`
              : "Nothing waiting"
          }
          to={paths.objectList({
            objectId: "finance_purchase_ledger.purchase_invoice",
          })}
        />
        {/* The figure is the count of the tab it opens, so the number a
              reviewer clicks is the number they land on. */}
        <StatCard
          title="Needs a look"
          value={String(counts.needs_review)}
          detail={`Variances, goods not in, duplicates and holds · ${counts.no_match} more with no PO match`}
          to={paths.objectList({
            objectId: "finance_purchase_ledger.purchase_invoice",
            search: "queue=needs_a_look",
          })}
          attention={counts.needs_review > 0}
        />
        <StatCard
          title={`Due in the next ${home.dueSoonDays} days`}
          value={String(home.dueSoonCount)}
          detail={
            home.dueSoonCount > 0
              ? `${formatMoney(home.dueSoonNet)} net — not paid until confirmed`
              : "Nothing due before it is confirmed"
          }
          to={
            dueSoon[0]
              ? paths.objectRecord({
                  objectId: "finance_purchase_ledger.purchase_invoice",
                  recordKey: dueSoon[0].id,
                })
              : paths.objectList({
                  objectId: "finance_purchase_ledger.purchase_invoice",
                })
          }
          attention={home.dueSoonCount > 0}
        />
        <StatCard
          title="Received, no invoice"
          value={String(home.receivedNoInvoiceCount)}
          detail={`${formatMoney(home.receivedNoInvoiceNet)} ordered · ${home.missingCount} ${home.missingCount === 1 ? "looks" : "look"} missing`}
          to={paths.purchaseOrders()}
          attention={home.missingCount > 0}
        />
        {hasMonthEnd && <MonthEndCard month={monthJustEnded(now)} />}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Due soon, still to confirm
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              Nothing unconfirmed reaches Xero, so these miss the payment run
              unless someone confirms them first.
            </p>
          </CardHeader>
          <CardContent className="px-0">
            {home.dueSoonCount === 0 ? (
              <p className="text-muted-foreground px-6 text-sm">
                Nothing falls due in the next {home.dueSoonDays} days.
              </p>
            ) : (
              <Table className="[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6">
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dueSoon.map((item) => {
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Link
                            to={paths.objectRecord({
                              objectId:
                                "finance_purchase_ledger.purchase_invoice",
                              recordKey: item.id,
                            })}
                            className="font-medium hover:underline"
                          >
                            {item.supplierName}
                          </Link>
                          <div className="text-muted-foreground text-xs">
                            {item.supplierDocumentNo} · {item.statusLabel}
                          </div>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "tabular-nums",
                            item.isOverdue && "text-red-600 dark:text-red-400",
                          )}
                        >
                          {formatDate(item.dueDate)}
                          {item.isOverdue && (
                            <div className="text-xs">Overdue</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(item.netAmount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Invoices that look missing
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              Goods received {home.chaseAfterDays} days or more ago, with no
              invoice anywhere — worth a word with the supplier.
            </p>
          </CardHeader>
          <CardContent className="px-0">
            {home.missingCount === 0 ? (
              <p className="text-muted-foreground px-6 text-sm">
                Every received order has its invoice, or one waiting to match.
              </p>
            ) : (
              <Table className="[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6">
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missing.map((po) => (
                    <TableRow key={po.id}>
                      <TableCell>
                        <Link
                          to={paths.purchaseOrders()}
                          className="font-medium hover:underline"
                        >
                          {po.supplierName}
                        </Link>
                        <div className="text-muted-foreground text-xs">
                          {po.purchaseOrderNo}
                          {po.orderNo ? ` · ${po.orderNo}` : ""}
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {po.lastReceivedOn && formatDate(po.lastReceivedOn)}
                        <div className="text-xs text-amber-600 dark:text-amber-500">
                          {po.lastReceivedOn &&
                            `${ageLabel(`${po.lastReceivedOn}T00:00:00Z`, now)} ago`}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(po.netAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/**
 * Month end: one line per trading company for the month just ended, each saying where its close stands and opening
 * that month end's record, with how many of its lines are still to decide when any are. The lines are the month end
 * list's own (`GET /month-ends?month=`), read through the object UI's adapter, so the card never words a stage or counts
 * a line itself.
 */
function MonthEndCard({ month }: { month: string }) {
  const objectUi = useObjectUiAdapter();
  const index = useObjectManifest();
  const object = index.objectOf(MONTH_END_OBJECT);
  const list = object.list;
  const stageLabels = object.record?.state_labels?.stage ?? {};
  const months = useLoad(
    () =>
      list
        ? objectUi.list(list, {
            ...emptyListQuery(),
            page_size: 50,
            sort: "trading_company_code",
            filters: { month },
          })
        : Promise.reject(new Error("The month ends have no list.")),
    month,
  );
  const title = `Month end: ${monthLabel(month)}`;
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-muted-foreground text-sm font-medium">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {months.error ? (
          <p className="text-muted-foreground text-xs">
            {accrualsErrorMessage(months.error, "The month ends did not load.")}
          </p>
        ) : !months.data ? (
          <Skeleton className="h-16 w-full" aria-label="Loading month end" />
        ) : months.data.items.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            No trading company has a month end to close.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {months.data.items.map((row) => {
              const stage = String(row.stage);
              const toDecide = Number(row.lines_to_decide ?? 0);
              return (
                <li key={String(row.month_end_id)}>
                  <Link
                    to={paths.objectRecord({
                      objectId: MONTH_END_OBJECT,
                      recordKey: String(row.month_end_id),
                    })}
                    className="hover:bg-muted/50 flex items-baseline justify-between gap-3 rounded px-1 text-sm"
                  >
                    <span className="font-medium">
                      {String(row.trading_company)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {stageLabels[stage] ?? stage}
                      {toDecide > 0 ? ` · ${toDecide} to decide` : ""}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({
  title,
  value,
  detail,
  to,
  attention = false,
}: {
  title: string;
  value: string;
  detail: string;
  to: string;
  /** Tints the figure when it is something to act on today. */
  attention?: boolean;
}) {
  return (
    <Link to={to} className="group block">
      <Card className="group-hover:border-primary/40 h-full transition-colors">
        <CardHeader className="pb-2">
          <CardTitle className="text-muted-foreground text-sm font-medium">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "text-3xl font-semibold tabular-nums",
              attention && "text-amber-600 dark:text-amber-500",
            )}
          >
            {value}
          </div>
          <p className="text-muted-foreground mt-1 text-xs">{detail}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
